import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './Reservation.css';

const Reservation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    collection: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const collections = [
    'Royal Series',
    'Sport Series',
    'Classic Series',
    'Not Sure - Need Guidance'
  ];

  const timeSlots = [
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM'
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\d\s\-\+\(\)]+$/;
    return phone.length >= 10 && re.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.collection) {
      newErrors.collection = 'Please select a collection';
    }

    if (!formData.preferredDate) {
      newErrors.preferredDate = 'Please select a date';
    }

    if (!formData.preferredTime) {
      newErrors.preferredTime = 'Please select a time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    soundManager.play('ui-click', 0.4);

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      soundManager.play('whoosh', 0.5);

      // Log form data (in real app, send to backend)
      console.log('Form submitted:', formData);

      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          collection: '',
          preferredDate: '',
          preferredTime: '',
          message: ''
        });
        setTouched({});
      }, 5000);
    }, 2000);
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <section 
      id="reservation" 
      ref={sectionRef}
      className={`reservation-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="reservation-container">
        {/* Section Header */}
        <div className="reservation-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Exclusive Access</span>
            <span className="label-line"></span>
          </div>
          <h2 className="reservation-title">Schedule a Private Viewing</h2>
          <p className="reservation-subtitle">
            Experience CHRONOS timepieces in person. Our specialists will guide 
            you through our collections in an exclusive, personalized consultation.
          </p>
        </div>

        <div className="reservation-content">
          {/* Left Side - Form */}
          <div className="form-section">
            {!isSubmitted ? (
              <form className="reservation-form" onSubmit={handleSubmit}>
                {/* Full Name */}
                <div className="form-group">
                  <label htmlFor="fullName" className="form-label">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${errors.fullName && touched.fullName ? 'error' : ''}`}
                    placeholder="Your Name"
                  />
                  {errors.fullName && touched.fullName && (
                    <span className="error-message">{errors.fullName}</span>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${errors.email && touched.email ? 'error' : ''}`}
                    placeholder="Your Email"
                  />
                  {errors.email && touched.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                {/* Phone */}
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${errors.phone && touched.phone ? 'error' : ''}`}
                    placeholder="+1 (555) 000-0000"
                  />
                  {errors.phone && touched.phone && (
                    <span className="error-message">{errors.phone}</span>
                  )}
                </div>

                {/* Collection */}
                <div className="form-group">
                  <label htmlFor="collection" className="form-label">
                    Preferred Collection <span className="required">*</span>
                  </label>
                  <select
                    id="collection"
                    name="collection"
                    value={formData.collection}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-select ${errors.collection && touched.collection ? 'error' : ''}`}
                  >
                    <option value="">Select a collection</option>
                    {collections.map((collection) => (
                      <option key={collection} value={collection}>
                        {collection}
                      </option>
                    ))}
                  </select>
                  {errors.collection && touched.collection && (
                    <span className="error-message">{errors.collection}</span>
                  )}
                </div>

                {/* Date and Time Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="preferredDate" className="form-label">
                      Preferred Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      min={getMinDate()}
                      className={`form-input ${errors.preferredDate && touched.preferredDate ? 'error' : ''}`}
                    />
                    {errors.preferredDate && touched.preferredDate && (
                      <span className="error-message">{errors.preferredDate}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferredTime" className="form-label">
                      Preferred Time <span className="required">*</span>
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`form-select ${errors.preferredTime && touched.preferredTime ? 'error' : ''}`}
                    >
                      <option value="">Select time</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                    {errors.preferredTime && touched.preferredTime && (
                      <span className="error-message">{errors.preferredTime}</span>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Tell us about your preferences or any specific models you'd like to see..."
                    rows="4"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      <span>Submitting Request...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Request</span>
                      <span className="button-arrow">→</span>
                    </>
                  )}
                </button>

                <p className="form-note">
                  We'll contact you within 24 hours to confirm your appointment.
                </p>
              </form>
            ) : (
              <div className="success-message">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="success-title">Request Submitted Successfully</h3>
                <p className="success-description">
                  Thank you for your interest in CHRONOS. Our team will contact 
                  you within 24 hours to confirm your private viewing appointment.
                </p>
                <div className="success-details">
                  <div className="detail-item">
                    <span className="detail-icon">◆</span>
                    <span className="detail-text">{formData.collection}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">📅</span>
                    <span className="detail-text">
                      {new Date(formData.preferredDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">🕐</span>
                    <span className="detail-text">{formData.preferredTime}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Info */}
          <div className="info-section">
            <div className="info-card">
              <h3 className="info-card-title">What to Expect</h3>
              <ul className="info-list">
                <li className="info-item">
                  <span className="info-bullet">◆</span>
                  <span className="info-text">
                    Private consultation with a CHRONOS specialist
                  </span>
                </li>
                <li className="info-item">
                  <span className="info-bullet">◆</span>
                  <span className="info-text">
                    Hands-on experience with multiple timepieces
                  </span>
                </li>
                <li className="info-item">
                  <span className="info-bullet">◆</span>
                  <span className="info-text">
                    Personalized guidance on movement and features
                  </span>
                </li>
                <li className="info-item">
                  <span className="info-bullet">◆</span>
                  <span className="info-text">
                    Complimentary refreshments in our boutique
                  </span>
                </li>
                <li className="info-item">
                  <span className="info-bullet">◆</span>
                  <span className="info-text">
                    No obligation to purchase
                  </span>
                </li>
              </ul>
            </div>

           <div className="info-card">
  <h3 className="info-card-title">Visit Our Boutique</h3>
  <div className="contact-info">
    <div className="contact-item">
      <span className="contact-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path 
            d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <circle 
            cx="12" 
            cy="10" 
            r="3" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="contact-text">
        <p className="contact-label">Address</p>
        <p className="contact-value">
          Rue du Rhône 45<br />
          1204 Geneva, Switzerland
        </p>
      </div>
    </div>

    <div className="contact-item">
      <span className="contact-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path 
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="contact-text">
        <p className="contact-label">Phone</p>
        <p className="contact-value">+41 22 XXX XXXX</p>
      </div>
    </div>

    <div className="contact-item">
      <span className="contact-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path 
            d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <polyline 
            points="22,6 12,13 2,6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="contact-text">
        <p className="contact-label">Email</p>
        <p className="contact-value">boutique@chronos.ch</p>
      </div>
    </div>

    <div className="contact-item">
      <span className="contact-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <circle 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <polyline 
            points="12 6 12 12 16 14" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="contact-text">
        <p className="contact-label">Hours</p>
        <p className="contact-value">
          Monday - Saturday<br />
          10:00 AM - 6:00 PM
        </p>
      </div>
    </div>
  </div>
</div>

<div className="info-card privacy-card">
  <span className="privacy-icon">
    <svg viewBox="0 0 24 24" fill="none">
      <rect 
        x="3" 
        y="11" 
        width="18" 
        height="11" 
        rx="2" 
        ry="2" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <path 
        d="M7 11V7a5 5 0 0 1 10 0v4" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  </span>
  <p className="privacy-text">
    Your information is secure and will never be shared with third parties. 
    We respect your privacy.
  </p>
</div>

            <div className="info-card privacy-card">
              <span className="privacy-icon">🔒</span>
              <p className="privacy-text">
                Your information is secure and will never be shared with third parties. 
                We respect your privacy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reservation;